import React, { useState, useEffect } from 'react';
import AdminTable from 'common/AdminTable';
import TruncatedMessage from 'common/TruncatedMessage';
import { getRequest, patchRequest, deleteRequest } from 'apis/ApiInstence';
import { FaTrash, FaEdit, FaEye } from 'react-icons/fa';
import { PRODUCT_OPTION_LABELS, PRODUCT_OPTION_PRESETS } from 'constants/productDropdownOptions';
import { toast } from 'react-toastify';
import { buildProductImageUrl, extractProductImages, getProductId } from './productImageUtils';

const BACKEND = process.env.REACT_APP_BACKEND;

const ProductList = () => {
  const OTHER_VALUE = '__other__';
  const NUMERIC_FIELDS = new Set(['selling_price', 'shipping_charge', 'original_price', 'stock_quantity', 'weight', 'rating']);

  const [productData, setProductData] = useState([]);
  const [categories, setCategories] = useState([]);
  const [mainCategories, setMainCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [isViewMode, setIsViewMode] = useState(false);
  const [currentProduct, setCurrentProduct] = useState(null);
  const [imageFiles, setImageFile] = useState([]);
  const [editOptionSelections, setEditOptionSelections] = useState({});
  const [editCustomOptionValues, setEditCustomOptionValues] = useState({});
  const [saving, setSaving] = useState(false);

  const formatOptionLabel = (fieldName, value) => {
    if (fieldName === 'weight') return `${value}kg`;
    return value;
  };

  const normalizeNumberValue = (fieldName, rawValue) => {
    const str = String(rawValue ?? '').trim();
    if (!str) return '';
    const cleaned = fieldName === 'weight' ? str.replace(/[^0-9.]/g, '') : str;
    const n = Number.parseFloat(cleaned);
    if (Number.isNaN(n)) return '';
    return String(n);
  };

  const getCategoryName = (id) => {
    const cat = categories.find((c) => Number(c.id) === Number(id));
    return cat ? cat.name : id || '';
  };

  const getMainCategoryName = (id) => {
    const cat = mainCategories.find((c) => Number(c.id) === Number(id));
    return cat ? cat.name : id || '';
  };

  const getSubCategoryName = (id) => {
    const cat = subCategories.find((c) => Number(c.id) === Number(id));
    return cat ? cat.name : id || '';
  };

  const closeModal = () => {
    setShowModal(false);
    setCurrentProduct(null);
    setImageFile([]);
    setSaving(false);
  };

  useEffect(() => {
    if (!showModal) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [showModal]);

  const fetchProducts = async () => {
    try {
      const res = await getRequest('products');
      const nextProducts = res?.data?.data ?? res?.data?.products ?? res?.data ?? [];
      setProductData(Array.isArray(nextProducts) ? nextProducts : []);
    } catch (err) {
      console.error('Failed to fetch products:', err);
      toast.error(err?.message || 'Failed to fetch products.');
    }
  };

  const fetchCategories = async () => {
    try {
      const [catRes, mainCatRes, subCatRes] = await Promise.all([
        getRequest('categories'),
        getRequest('main-categories'),
        getRequest('sub-categories'),
      ]);
      setCategories(catRes?.data || []);
      setMainCategories(mainCatRes?.data || []);
      setSubCategories(subCatRes?.data || []);
    } catch (err) {
      console.error('Failed to fetch category data:', err);
    }
  };

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, []);

  const selectFields = ['weight', 'rating'];

  const renderOptionSelect = (field) => {
    const options = PRODUCT_OPTION_PRESETS[field] || [];
    const label = PRODUCT_OPTION_LABELS[field] || field.replace('_', ' ');
    const selectedValue = editOptionSelections[field] ?? '';
    const customValue = editCustomOptionValues[field] ?? '';

    const setCurrentFieldValue = (value) => {
      setCurrentProduct((prev) => ({ ...prev, [field]: value }));
    };

    const handleSelectChange = (value) => {
      setEditOptionSelections((prev) => ({ ...prev, [field]: value }));

      if (!value) {
        setEditCustomOptionValues((prev) => ({ ...prev, [field]: '' }));
        setCurrentFieldValue('');
        return;
      }

      if (value === OTHER_VALUE) {
        setCurrentFieldValue(editCustomOptionValues[field] || '');
        return;
      }

      setEditCustomOptionValues((prev) => ({ ...prev, [field]: '' }));
      setCurrentFieldValue(value);
    };

    const handleCustomChange = (value) => {
      setEditCustomOptionValues((prev) => ({ ...prev, [field]: value }));
      setCurrentFieldValue(value);
    };

    return (
      <>
        <select
          id={field}
          className="form-control"
          value={selectedValue}
          onChange={(e) => handleSelectChange(e.target.value)}
        >
          <option value="">{`Select ${label}`}</option>
          {options.map((value) => (
            <option key={value} value={value}>
              {formatOptionLabel(field, value)}
            </option>
          ))}
          <option value={OTHER_VALUE}>Other (Type)</option>
        </select>

        {selectedValue === OTHER_VALUE && (
          <input
            type="text"
            className="form-control mt-2"
            placeholder={`Enter ${label}`}
            value={customValue}
            onChange={(e) => handleCustomChange(e.target.value)}
          />
        )}
      </>
    );
  };

  const openModal = (product, viewOnly = false) => {
    setCurrentProduct({ ...product });
    setIsViewMode(viewOnly);
    setImageFile([]);

    const nextSelections = {};
    const nextCustom = {};
    for (const field of selectFields) {
      const value = product?.[field] ?? '';
      const valueString = value === '' ? '' : String(value);
      const presets = PRODUCT_OPTION_PRESETS[field] || [];

      if (!valueString) {
        nextSelections[field] = '';
        nextCustom[field] = '';
      } else if (presets.includes(valueString)) {
        nextSelections[field] = valueString;
        nextCustom[field] = '';
      } else {
        const normalizedForNumberFields = NUMERIC_FIELDS.has(field) ? normalizeNumberValue(field, valueString) : valueString;
        const matchesPreset = normalizedForNumberFields && presets.includes(normalizedForNumberFields);
        if (matchesPreset) {
          nextSelections[field] = normalizedForNumberFields;
          nextCustom[field] = '';
          continue;
        }
        nextSelections[field] = OTHER_VALUE;
        nextCustom[field] = normalizedForNumberFields || valueString;
      }
    }

    setEditOptionSelections(nextSelections);
    setEditCustomOptionValues(nextCustom);
    setShowModal(true);
  };

  const handleSave = async () => {
    const productId = getProductId(currentProduct);
    if (!productId) {
      toast.error('Missing product ID!');
      return;
    }
    if (saving) return;

    const formData = new FormData();
    const appendIf = (key, value) => { if (value !== '' && value != null) formData.append(key, value); };

    appendIf('name', currentProduct.name || '');
    appendIf('selling_price', normalizeNumberValue('selling_price', currentProduct.selling_price));
    appendIf('shipping_charge', normalizeNumberValue('shipping_charge', currentProduct.shipping_charge));
    appendIf('original_price', normalizeNumberValue('original_price', currentProduct.original_price));
    appendIf('description', currentProduct.description || '');
    appendIf('benefit_description', currentProduct.benefit_description || '');
    appendIf('category_id', currentProduct.category_id);
    appendIf('main_category_id', currentProduct.main_category_id);
    appendIf('sub_category_id', currentProduct.sub_category_id);
    appendIf('stock_quantity', normalizeNumberValue('stock_quantity', currentProduct.stock_quantity));
    appendIf('weight', normalizeNumberValue('weight', currentProduct.weight));
    appendIf('status', currentProduct.status);
    appendIf('rating', normalizeNumberValue('rating', currentProduct.rating));

    if (imageFiles.length > 0) {
      imageFiles.forEach((file) => formData.append('images', file));
    }

    try {
      setSaving(true);
      await patchRequest(`products/${productId}`, formData);

      toast.success('Product updated successfully!');
      fetchProducts();
      closeModal();
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        (typeof err?.response?.data === 'string' ? err.response.data : '') ||
        err?.message ||
        'Failed to update product';
      toast.error(msg);
      setSaving(false);
      console.error('Failed to update product:', err?.response?.data || err);
    }
  };

  const handleRemove = async (id) => {
    if (!id) {
      toast.error('Missing product ID!');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      await deleteRequest(`products/${id}`);
      fetchProducts();
    } catch (err) {
      console.error('Failed to delete product:', err);
    }
  };

  const tableHeaders = [
    {
      Header: 'Image',
      accessor: 'images',
      Cell: ({ row }) => {
        const list = extractProductImages(row.original);
        if (list.length === 0) return <span>No Image</span>;
        return (
          <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
            {list.map((img, i) => (
              <img
                key={i}
                src={buildProductImageUrl(img, BACKEND)}
                alt={`img-${i}`}
                width={50}
                height={50}
                style={{ objectFit: 'cover', borderRadius: 4 }}
              />
            ))}
          </div>
        );
      }
    },
    { Header: 'Name', accessor: 'name' },
    { Header: 'Price', accessor: 'selling_price' },
    { Header: 'Shipping', accessor: 'shipping_charge' },
    { Header: 'Original', accessor: 'original_price' },
    { Header: 'Stock', accessor: 'stock_quantity' },
    { Header: 'Weight', accessor: 'weight' },
    { Header: 'Rating', accessor: 'rating' },
    { Header: 'Status', accessor: 'status' },
    {
      Header: 'Category',
      accessor: 'category_id',
      Cell: ({ row }) => getCategoryName(row.original.category_id)
    },
    {
      Header: 'Main Category',
      accessor: 'main_category_id',
      Cell: ({ row }) => getMainCategoryName(row.original.main_category_id)
    },
    {
      Header: 'Sub Category',
      accessor: 'sub_category_id',
      Cell: ({ row }) => getSubCategoryName(row.original.sub_category_id)
    },
    {
      Header: 'Description',
      accessor: 'description',
      Cell: ({ row }) => <TruncatedMessage text={row?.original?.description} maxLength={10} />
    },
    {
      Header: 'Actions',
      accessor: 'actions',
      Cell: ({ row }) => (
        <>
          <button
            className="btn btn-sm mx-1 text-white"
            style={{ backgroundColor: '#17a2b8' }}
            onClick={() => openModal(row.original, true)}
          >
            <FaEye />
          </button>
          <button
            className="btn btn-sm mx-1 text-white"
            style={{ backgroundColor: '#ffc107' }}
            onClick={() => openModal(row.original, false)}
          >
            <FaEdit />
          </button>
          <button
            className="btn btn-sm mx-1 text-white"
            style={{ backgroundColor: '#dc3545' }}
            onClick={() => handleRemove(getProductId(row.original))}
          >
            <FaTrash />
          </button>
        </>
      )
    }
  ];

  return (
    <>
      <div className="container-fluid mt-5">
        <div className="mainheadig mx-4">
          <h4 className="text-white font-weight-bold">Product List</h4>
        </div>

        {productData?.length ? (
          <AdminTable tableHeaders={tableHeaders} tableData={productData} />
        ) : (
          <div className="text-white mx-4">No product records found.</div>
        )}
      </div>

      {showModal && currentProduct && (
        <>
          <button
            type="button"
            className="modal-backdrop fade show border-0 p-0"
            aria-label="Close modal"
            onClick={closeModal}
          />
          <div className="modal fade show" style={{ display: 'block' }} role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{isViewMode ? 'See All Details' : 'Update Product'}</h5>
                <button className="close" onClick={closeModal}>
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>

              <div className="modal-body">
                {isViewMode ? (
                  <>
                    {['name', 'selling_price', 'shipping_charge', 'original_price', 'stock_quantity', 'weight', 'rating', 'status', 'category_id', 'main_category_id', 'sub_category_id', 'description', 'benefit_description'].map((field) => (
                      <div className="form-group mb-2" key={field}>
                        <label htmlFor={field} className="admin-label">
                          {field.replace(/_/g, ' ')}
                        </label>
                        <input
                          id={field}
                          type="text"
                          className="form-control"
                          value={currentProduct[field] ?? ''}
                          readOnly
                        />
                      </div>
                    ))}
                  </>
                ) : (
                  <>
                    <div className="row">
                      {['name', 'selling_price', 'shipping_charge', 'original_price', 'stock_quantity', 'weight', 'rating', 'status'].map(
                        (field) => (
                          <div className="col-md-4 mb-3" key={field}>
                            <label htmlFor={field} className="admin-label">
                              {field.replace(/_/g, ' ')}
                            </label>
                            {field === 'status' ? (
                              <select
                                id="status"
                                className="form-control"
                                value={currentProduct.status || 'active'}
                                onChange={(e) => setCurrentProduct({ ...currentProduct, status: e.target.value })}
                              >
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                              </select>
                            ) : selectFields.includes(field) ? (
                              renderOptionSelect(field)
                            ) : (
                              <input
                                id={field}
                                type={/price|quantity|weight|shipping|rating/.test(field) ? 'number' : 'text'}
                                step={field === 'rating' ? '0.1' : undefined}
                                className="form-control"
                                value={currentProduct[field] || ''}
                                onChange={(e) => setCurrentProduct({ ...currentProduct, [field]: e.target.value })}
                              />
                            )}
                          </div>
                        )
                      )}
                      <div className="col-md-4 mb-3">
                        <label htmlFor="modalCategory" className="admin-label">Category</label>
                        <select
                          id="modalCategory"
                          className="form-control"
                          value={currentProduct.category_id || ''}
                          onChange={(e) => setCurrentProduct({ ...currentProduct, category_id: e.target.value })}
                        >
                          <option value="">Select Category</option>
                          {categories.map((cat) => (
                            <option key={cat.id} value={cat.id}>{cat.name}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-4 mb-3">
                        <label htmlFor="modalMainCategory" className="admin-label">Main Category</label>
                        <select
                          id="modalMainCategory"
                          className="form-control"
                          value={currentProduct.main_category_id || ''}
                          onChange={(e) => setCurrentProduct({ ...currentProduct, main_category_id: e.target.value })}
                        >
                          <option value="">Select Main Category</option>
                          {mainCategories.map((cat) => (
                            <option key={cat.id} value={cat.id}>{cat.name}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-4 mb-3">
                        <label htmlFor="modalSubCategory" className="admin-label">Sub Category</label>
                        <select
                          id="modalSubCategory"
                          className="form-control"
                          value={currentProduct.sub_category_id || ''}
                          onChange={(e) => setCurrentProduct({ ...currentProduct, sub_category_id: e.target.value })}
                        >
                          <option value="">Select Sub Category</option>
                          {subCategories.filter((s) => !currentProduct.category_id || Number(s.category_id) === Number(currentProduct.category_id)).map((cat) => (
                            <option key={cat.id} value={cat.id}>{cat.name}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-12 mb-3">
                        <label htmlFor="description" className="admin-label">Description</label>
                        <textarea
                          id="description"
                          rows={2}
                          className="form-control"
                          value={currentProduct.description || ''}
                          onChange={(e) => setCurrentProduct({ ...currentProduct, description: e.target.value })}
                        />
                      </div>
                      <div className="col-12 mb-3">
                        <label htmlFor="benefit_description" className="admin-label">Benefit Description</label>
                        <textarea
                          id="benefit_description"
                          rows={2}
                          className="form-control"
                          value={currentProduct.benefit_description || ''}
                          onChange={(e) => setCurrentProduct({ ...currentProduct, benefit_description: e.target.value })}
                        />
                      </div>
                    </div>

                    <div className="form-group mb-2">
                      <label htmlFor="product-image" className="admin-label">Image</label>
                      <input
                        id="product-image"
                        type="file"
                        multiple
                        className="form-control"
                        onChange={(e) => setImageFile(Array.from(e.target.files))}
                      />

                      <div className="mt-2">
                        {imageFiles.length > 0 ? (
                          <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                            {imageFiles.map((file, i) => (
                              <img
                                key={i}
                                src={URL.createObjectURL(file)}
                                width={50}
                                height={50}
                                className="border rounded"
                                alt={`preview-${i}`}
                                style={{ objectFit: 'cover' }}
                              />
                            ))}
                          </div>
                        ) : extractProductImages(currentProduct).length > 0 ? (
                          (() => {
                            const imgs = extractProductImages(currentProduct);
                            return (
                              <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                                {imgs.map((img, i) => (
                                  <img
                                    key={i}
                                    src={buildProductImageUrl(img, BACKEND)}
                                    width={50}
                                    height={50}
                                    className="border rounded"
                                    alt={`img-${i}`}
                                    style={{ objectFit: 'cover' }}
                                  />
                                ))}
                              </div>
                            );
                          })()
                        ) : (
                          <span className="text-muted">No preview</span>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>

              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={closeModal} disabled={saving}>
                  Close
                </button>
                {!isViewMode && (
                  <button className="btn addbtn" onClick={handleSave} disabled={saving}>
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                )}
              </div>
            </div>
          </div>
          </div>
        </>
      )}
    </>
  );
};

export default ProductList;
